"use client";

import { useIdentity } from "@/lib/providers/identity";
import { useIsFeatureEnabled } from "@/entities/system-configuration";
import { useIdleReady } from "@/shared/hooks/use-idle-ready";
import { useChatRealtime } from "../model/use-chat-realtime";
import { useCallStore } from "../model/call-store";

/**
 * Headless, app-wide mount point for the chat realtime subscription. Renders
 * nothing — it exists so the Chime SDK Messaging WebSocket is opened exactly
 * ONCE per tab (mounting it inside a widget would open a session per instance).
 *
 * Gated on an authenticated identity AND the chat_messaging feature flag. The
 * browser holds one MessagingSession WebSocket straight to Chime (never to a
 * Vercel function) and the server only PUBLISHES events, so the cost is a
 * single idle socket per tab — but orgs without chat still shouldn't open one.
 * The flag gates this bridge, not just the nav links: the retired SSE + Upstash
 * transport held a serverless function for its full 300s lifetime with a
 * per-connection Upstash poll + presence heartbeat, and every signed-in tab
 * paid that cost while the flag gated only the links (chat-cost investigation
 * 2026-07-16, which drove the move to Chime Messaging).
 *
 * Connecting is deferred to the first idle callback. Opening the socket costs a
 * grant RPC plus a new AWS SDK client, and this bridge mounts from the root
 * layout, so on a cold load that work landed in the same commit as the first
 * paint of whatever page the user asked for. Nothing is lost by waiting: an
 * inbound event during those few hundred milliseconds is picked up by the
 * queries' own refetch. A call already in flight (a remount mid-call) skips the
 * wait and connects on the first render.
 */
export function ChatRealtimeBridge(): null {
  const {
    identity
  } = useIdentity();
  const isChatEnabled = useIsFeatureEnabled("chat_messaging");
  // Snapshot, not a subscription: useIdleReady only reads this on first render,
  // and subscribing here would re-render the whole bridge on every call phase.
  const isIdleReady = useIdleReady(useCallStore.getState().phase !== "idle");
  useChatRealtime(Boolean(identity) && isChatEnabled && isIdleReady);
  return null;
}